import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { loyaltyCampaign, restaurantConfig } from "@/config/restaurantConfig";
import { useOrder } from "@/context/OrderContext";
import { useSession } from "@/context/SessionContext";
import { scopedKey, readJSON, writeJSON } from "@/lib/storage";
import { isCampaignActive, joinMember, migrateMember, redeemAndSync, syncMember } from "@/lib/loyalty";

const LoyaltyContext = createContext(null);

// Organization-wide campaigns share one card across branches; others are branch-scoped.
const storageKey = () => scopedKey("loyalty", loyaltyCampaign.branchScope === "organization" ? { branchId: null } : {});

// Mock loyalty identity + progress. NOT secure production identity — a real
// backend must verify phone identity and enforce one-count-per-order, atomic
// redemption, campaign/branch scope, and order eligibility server-side.
export function LoyaltyProvider({ children }) {
  const { orders } = useOrder();
  const { sessionId } = useSession();
  const [member, setMember] = useState(() => migrateMember(readJSON(storageKey(), null), loyaltyCampaign));

  useEffect(() => { writeJSON(storageKey(), member); }, [member]);

  const enabled = isCampaignActive(loyaltyCampaign, { branchId: restaurantConfig.branch.id });
  const threshold = loyaltyCampaign.threshold;

  // Credit orders as they reach COMPLETED (idempotent — see lib/loyalty).
  useEffect(() => {
    setMember((m) => {
      const next = syncMember(m, orders, loyaltyCampaign);
      return next === m ? m : next;
    });
  }, [orders]);

  const join = useCallback((phone) => {
    setMember(joinMember(phone, orders, loyaltyCampaign, { sessionId }));
  }, [orders, sessionId]);

  const redeem = useCallback(() => setMember((m) => redeemAndSync(m, orders, loyaltyCampaign)), [orders]);
  const remove = useCallback(() => setMember(null), []);

  const value = useMemo(
    () => ({
      enabled, threshold, member, maskedPhone: member?.phoneMasked || "",
      join, redeem, remove, campaign: loyaltyCampaign,
    }),
    [enabled, threshold, member, join, redeem, remove]
  );
  return <LoyaltyContext.Provider value={value}>{children}</LoyaltyContext.Provider>;
}

export function useLoyalty() {
  const ctx = useContext(LoyaltyContext);
  if (!ctx) throw new Error("useLoyalty must be used within LoyaltyProvider");
  return ctx;
}
